/**
 * 业务接口统一出口
 * - USE_MOCK=true 时全部直接返回 Mock 数据（仅「接口测试」按钮请求真实地址）
 * - USE_MOCK=false 时走真实接口，失败自动降级为 Mock，保证页面正常展示
 */

import { http } from './request';
import { USE_MOCK } from './env';
import {
  mockCompanyInfo,
  mockProducts,
  mockContactInfo,
  mockHomeData,
  mockActivities,
  mockCoupons,
  mockPaidServices,
} from './mock';

/**
 * 首页聚合数据（/off/home）
 * 测试环境地址：http://192.168.1.105:8081/off/home
 */
export async function fetchHomeData() {
  if (USE_MOCK) return mockHomeData;
  try {
    return await http.get('/off/home');
  } catch (e) {
    console.warn('[API降级] /off/home 使用Mock数据：', e.message);
    return mockHomeData;
  }
}

/** 获取公司信息（关于我们） */
export async function fetchCompanyInfo() {
  if (USE_MOCK) return mockCompanyInfo;
  try {
    return await http.get('/company/info');
  } catch (e) {
    console.warn('[API降级] /company/info 使用Mock数据：', e.message);
    return mockCompanyInfo;
  }
}

/**
 * 获取产品服务列表
 * @param {string} category 分类：all | furniture | survey
 */
export async function fetchProducts(category = 'all') {
  if (USE_MOCK) {
    return category === 'all'
      ? mockProducts
      : mockProducts.filter((p) => p.category === category);
  }
  try {
    return await http.get('/products', { category });
  } catch (e) {
    console.warn('[API降级] /products 使用Mock数据：', e.message);
    return category === 'all'
      ? mockProducts
      : mockProducts.filter((p) => p.category === category);
  }
}

/** 获取联系方式 */
export async function fetchContactInfo() {
  if (USE_MOCK) return mockContactInfo;
  try {
    return await http.get('/contact/info');
  } catch (e) {
    console.warn('[API降级] /contact/info 使用Mock数据：', e.message);
    return mockContactInfo;
  }
}

/**
 * 用户登录
 * @param {string} username 用户名 / 手机号
 * @param {string} password 密码
 * @returns {Promise<{name:string, token:string}>}
 */
export async function login(username, password) {
  if (USE_MOCK) {
    // 演示环境：任意账号密码模拟登录成功
    await new Promise((r) => setTimeout(r, 500));
    return { name: username, token: `mock-token-${Date.now()}` };
  }
  try {
    return await http.post('/auth/login', { username, password });
  } catch (e) {
    console.warn('[API降级] /auth/login 模拟登录成功：', e.message);
    await new Promise((r) => setTimeout(r, 500));
    return { name: username, token: `mock-token-${Date.now()}` };
  }
}

/**
 * 获取产品 / 服务详情
 * @param {string|number} id 产品 id
 */
export async function fetchProductDetail(id) {
  const numId = Number(id);
  const detail = mockProducts.find((p) => p.id === numId);
  if (USE_MOCK) return detail || null;
  try {
    return await http.get(`/products/${numId}`);
  } catch (e) {
    console.warn('[API降级] /products/:id 使用Mock数据：', e.message);
    return detail || null;
  }
}

/** 获取公司发布的活动列表 */
export async function fetchActivities() {
  if (USE_MOCK) return mockActivities;
  try {
    return await http.get('/activities');
  } catch (e) {
    console.warn('[API降级] /activities 使用Mock数据：', e.message);
    return mockActivities;
  }
}

/** 获取我的优惠券 */
export async function fetchCoupons() {
  if (USE_MOCK) return mockCoupons;
  try {
    return await http.get('/user/coupons');
  } catch (e) {
    console.warn('[API降级] /user/coupons 使用Mock数据：', e.message);
    return mockCoupons;
  }
}

/** 获取首页付费尊享服务列表 */
export async function fetchPaidServices() {
  if (USE_MOCK) return mockPaidServices;
  try {
    return await http.get('/paid-services');
  } catch (e) {
    console.warn('[API降级] /paid-services 使用Mock数据：', e.message);
    return mockPaidServices;
  }
}

/**
 * 用户注册（演示环境：模拟注册成功并直接登录）
 * @param {string} username 用户名 / 手机号
 * @param {string} password 密码
 * @returns {Promise<{name:string, phone:string, token:string}>}
 */
export async function register(username, password) {
  if (USE_MOCK) {
    await new Promise((r) => setTimeout(r, 500));
    return { name: username, phone: username, token: `mock-token-${Date.now()}` };
  }
  try {
    return await http.post('/auth/register', { username, password });
  } catch (e) {
    console.warn('[API降级] /auth/register 模拟注册成功：', e.message);
    await new Promise((r) => setTimeout(r, 500));
    return { name: username, phone: username, token: `mock-token-${Date.now()}` };
  }
}
